// Feature 012: creating a post with versions, in two steps.
// 1. Write: a rail of versions (A–E) on the left, the selected version's text, photos/videos, link
//    and tags in the middle, and the kind, time and channels on the right; each channel shows how
//    the selected version fits its network's text limit.
// 2. Channels & time: which version each channel gets (or "Later", to choose on the calendar card)
//    and an optional time of its own.
// Create makes the group, uploads each version's media, and hands the chosen assignments to the
// group window, which previews them for confirmation; nothing is scheduled without that.
import React, {useState} from "react";
import Modal from "react-bootstrap/Modal";
import {createVariantGroup, saveVariantMedia} from "../../../api";
import {VariantAssignment, VariantGroupError, VariantGroupType} from "../../../redux/Content/VariantGroup/types";
import {PeerActivityModeCode} from "../../../redux/Content/PeerActivityMode/types";
import {GROUP_MODES, measureText, modeNeedsMedia} from "../../../components/Calendar/VariantGroupCard.helpers";
import {getHandlerIconByCode} from "../../../helpers/functions";
import {
    ChannelOption, LimitFor, MAX_VARIANTS, MIN_VARIANTS, Translate, VERSION_LABELS,
    fromLocalInput, takesMode, toLocalInput,
} from "./VariantGroup.shared";

interface Props {
    t: Translate;
    createAt: Date;
    channels: ChannelOption[];
    limitFor?: LimitFor;
    onClose: () => void;
    /** The new group, and the versions picked for channels in step 2 (to preview and confirm). */
    onCreated: (group: VariantGroupType, assignments: VariantAssignment[]) => void;
}

interface Version {
    text: string;
    tags: string;
    link: string;
    files: File[];
}

const LATER = '';

const emptyVersion = (): Version => ({text: '', tags: '', link: '', files: []});

const errorOf = (e: any): VariantGroupError => (e && typeof e === 'object' ? e : {success: false, message: String(e)});

const ChannelAvatar: React.FC<{ channel: ChannelOption }> = ({channel}) => (
    <span className="avatar-xs flex-shrink-0 position-relative variantGroupCreate__avatar">
        {channel.avatar
            ? <img src={channel.avatar} className="rounded-circle img-fluid" alt=""/>
            : <span className="avatar-title rounded-circle">{channel.name[0]}</span>}
        <img src={getHandlerIconByCode(channel.network)} className="variantGroupCreate__network rounded-circle" alt=""/>
    </span>
);

const VariantGroupCreate: React.FC<Props> = ({t, createAt, channels, limitFor, onClose, onCreated}) => {
    const [step, setStep] = useState<'write' | 'assign'>('write');
    const [mode, setMode] = useState<string>(PeerActivityModeCode.ACTIVITY);
    const [title, setTitle] = useState('');
    const [at, setAt] = useState(toLocalInput(createAt));
    const [versions, setVersions] = useState<Version[]>([emptyVersion(), emptyVersion()]);
    const [active, setActive] = useState(0);
    const [selected, setSelected] = useState<string[]>([]);
    // Step 2: channel uuid → version label ('' = later), and channel uuid → its own time.
    const [picks, setPicks] = useState<Record<string, string>>({});
    const [ownTimes, setOwnTimes] = useState<Record<string, string>>({});
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    // The group once made: pressing Create again after a failed upload retries the media only.
    const [created, setCreated] = useState<VariantGroupType | null>(null);
    // One key per form: a double submit or retry does not make a second group.
    const [requestKey] = useState(() => 'web-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 8));

    const offered = channels.filter((channel) => takesMode(channel, mode));
    const chosen = offered.filter((channel) => selected.includes(channel.uuid));
    const version = versions[active];
    const label = VERSION_LABELS[active];
    const hasContent = (v: Version) => v.text.trim() !== '' || v.files.length > 0;
    const writeValid = title.trim() !== '' && chosen.length > 0 && versions.length >= MIN_VARIANTS && versions.every(hasContent);

    const update = (changes: Partial<Version>) =>
        setVersions((prev) => prev.map((v, i) => (i === active ? {...v, ...changes} : v)));

    const fit = (channel: ChannelOption, v: Version) => {
        const max = limitFor ? limitFor(channel, mode, v.files.length > 0) : null;
        const length = measureText(v.text, v.tags);
        return {length, max, over: max !== null && length > max};
    };

    const changeMode = (code: string) => {
        setMode(code);
        // A channel that cannot take the new kind leaves the selection.
        setSelected((prev) => prev.filter((uuid) => channels.some((c) => c.uuid === uuid && takesMode(c, code))));
    };

    const removeVersion = () => {
        setVersions((prev) => prev.filter((_, i) => i !== active));
        setActive((prev) => Math.max(0, prev - 1));
        // Labels follow the order, so picks of the removed version and later ones no longer hold.
        setPicks({});
    };

    const assignments = (): VariantAssignment[] => chosen
        .filter((channel) => (picks[channel.uuid] ?? LATER) !== LATER)
        .map((channel) => ({
            channel: channel.uuid,
            variant: picks[channel.uuid],
            ...(ownTimes[channel.uuid] ? {publishAt: fromLocalInput(ownTimes[channel.uuid])} : {}),
        }));

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            const group: VariantGroupType = created ?? (await createVariantGroup({
                title: title.trim(),
                publishAt: fromLocalInput(at),
                mode,
                channels: chosen.map((c) => c.uuid),
                variants: versions.map((v) => ({text: v.text, tags: v.tags, link: v.link})),
                requestKey,
            }) as any).item;
            setCreated(group);
            let latest = group;
            for (let i = 0; i < versions.length; i++) {
                if (versions[i].files.length > 0) {
                    try {
                        latest = (await saveVariantMedia(group.uuid, VERSION_LABELS[i], versions[i].files) as any).item ?? latest;
                    } catch (e) {
                        setError(t("variantGroup:mediaUploadFailed", "The group was created, but the photos or videos did not upload. Press Create again to retry."));
                        return;
                    }
                }
            }
            onCreated(latest, assignments());
        } catch (e) {
            setError(errorOf(e).message || t("variantGroup:createFailed", "Could not create the group."));
        } finally {
            setBusy(false);
        }
    };

    const stepper = (
        <div className="variantGroupCreate__steps">
            <span className={"variantGroupCreate__step" + (step === 'write' ? " variantGroupCreate__step--on" : " variantGroupCreate__step--done")}>
                <span className="variantGroupCreate__num">{step === 'write' ? '1' : '✓'}</span>{t("variantGroup:stepWrite", "Write versions")}
            </span>
            <span className={"variantGroupCreate__bar" + (step === 'assign' ? " variantGroupCreate__bar--done" : "")}/>
            <span className={"variantGroupCreate__step" + (step === 'assign' ? " variantGroupCreate__step--on" : "")}>
                <span className="variantGroupCreate__num">2</span>{t("variantGroup:stepAssign", "Channels & time")}
            </span>
        </div>
    );

    const writeStep = (
        <div className="variantGroupCreate__write">
            <div className="variantGroupCreate__rail">
                <div className="variantGroupCreate__label">{t("variantGroup:versions", "versions")}</div>
                {versions.map((v, i) => (
                    <button key={i} type="button" data-testid={`version-${VERSION_LABELS[i]}`}
                            className={"variantGroupCreate__railItem" + (i === active ? " variantGroupCreate__railItem--on" : "")}
                            onClick={() => setActive(i)}>
                        <span className="variantGroupCreate__railHead">
                            <strong>{VERSION_LABELS[i]}</strong>
                            <span className="text-muted small ms-auto">
                                {measureText(v.text, v.tags)}{v.files.length > 0 && " · " + v.files.length + " " + t("variantGroup:media", "media")}
                            </span>
                        </span>
                        <span className="variantGroupCreate__railText">{v.text || "—"}</span>
                    </button>
                ))}
                {versions.length < MAX_VARIANTS &&
                    <button type="button" className="variantGroupCreate__railAdd" onClick={() => {
                        setVersions((prev) => [...prev, emptyVersion()]);
                        setActive(versions.length);
                    }}>
                        + {t("variantGroup:addVersion", "Add version")}
                    </button>}
            </div>

            <div className="variantGroupCreate__editor">
                <div className="d-flex align-items-center">
                    <span className="variantGroupCreate__label flex-grow-1">{t("variantGroup:version", "Version")} {label}</span>
                    {versions.length > MIN_VARIANTS &&
                        <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={removeVersion}>{t("common:remove", "Remove")}</button>}
                </div>
                <textarea className="form-control" rows={7} value={version.text}
                          aria-label={t("variantGroup:version", "Version") + " " + label}
                          onChange={(e) => update({text: e.target.value})}/>
                <div className="text-muted small text-end">{measureText(version.text, version.tags)}</div>
                <label className="variantGroupCreate__drop">
                    <input type="file" multiple accept="image/*,video/*" className="visually-hidden"
                           aria-label={t("variantGroup:mediaFor", "Photos and videos") + " " + label}
                           onChange={(e) => {
                               const files = Array.from(e.target.files || []);
                               update({files: [...version.files, ...files]});
                               e.target.value = '';
                           }}/>
                    <i className="ri-upload-cloud-2-line fs-24" aria-hidden="true"/>
                    <span className="fw-semibold">{t("variantGroup:mediaFor", "Photos and videos")} · {label}</span>
                    <span className="text-muted small">{t("variantGroup:dropFiles", "Click to choose photos or videos")}</span>
                </label>
                {version.files.length > 0 &&
                    <div className="d-flex flex-wrap gap-2">
                        {version.files.map((file, i) => (
                            <span key={file.name + i} className="badge bg-light text-body d-inline-flex align-items-center gap-1">
                                {file.name}
                                <button type="button" className="btn-close btn-close-sm" aria-label={t("variantGroup:removeFile", "Remove file") + " " + file.name}
                                        onClick={() => update({files: version.files.filter((_, j) => j !== i)})}/>
                            </span>
                        ))}
                    </div>}
                <input className="form-control" value={version.link} placeholder={t("variantGroup:link", "Link")}
                       aria-label={t("variantGroup:link", "Link")} onChange={(e) => update({link: e.target.value})}/>
                <input className="form-control" value={version.tags} placeholder={t("variantGroup:tags", "Tags")}
                       aria-label={t("variantGroup:tags", "Tags")} onChange={(e) => update({tags: e.target.value})}/>
            </div>

            <div className="variantGroupCreate__side">
                <div className="btn-group d-flex" role="group" aria-label={t("variantGroup:kind", "Kind")}>
                    {GROUP_MODES.map((code) => (
                        <button key={code} type="button" aria-pressed={mode === code}
                                className={"btn btn-sm " + (mode === code ? "btn-primary" : "btn-outline-primary")}
                                onClick={() => changeMode(code)}>
                            {t("peerActivityMode:" + code, code)}
                        </button>
                    ))}
                </div>
                {modeNeedsMedia(mode) &&
                    <div className="alert alert-info py-2 small mb-0">{t("variantGroup:mediaHintCreate", "Reels and stories need a photo or video in every version.")}</div>}
                <label className="variantGroupCreate__label" htmlFor="variantGroupCreate-time">{t("variantGroup:time", "Time")}</label>
                <input id="variantGroupCreate-time" type="datetime-local" className="form-control" value={at} onChange={(e) => setAt(e.target.value)}/>
                <div className="d-flex align-items-baseline">
                    <span className="variantGroupCreate__label flex-grow-1">{t("variantGroup:channels", "Channels")}</span>
                    <span className="text-muted small">{t("variantGroup:fitOf", "fit of version")} {label}</span>
                </div>
                <div className="variantGroupCreate__channels">
                    {offered.length === 0 && <div className="text-muted small p-3">{t("variantGroup:noChannelsForKind", "None of your channels takes this kind.")}</div>}
                    {offered.map((channel) => {
                        const f = fit(channel, version);
                        return (
                            <label key={channel.uuid} className="variantGroupCreate__chan">
                                <ChannelAvatar channel={channel}/>
                                <span className="flex-grow-1 mw-0">
                                    <span className="d-block text-truncate fw-semibold">{channel.name}</span>
                                    <span className="d-block text-muted small">{channel.network}</span>
                                </span>
                                {f.max !== null &&
                                    <span data-testid={`fit-${channel.uuid}`} className={"badge " + (f.over ? "bg-warning-subtle text-warning" : "bg-success-subtle text-success")}>
                                        {f.length}/{f.max}
                                    </span>}
                                <span className="form-check form-switch m-0">
                                    <input type="checkbox" role="switch" className="form-check-input" checked={selected.includes(channel.uuid)}
                                           aria-label={channel.name + " " + channel.network}
                                           onChange={(e) => setSelected((prev) => e.target.checked ? [...prev, channel.uuid] : prev.filter((u) => u !== channel.uuid))}/>
                                </span>
                            </label>
                        );
                    })}
                </div>
            </div>
        </div>
    );

    const assignStep = (
        <div className="variantGroupCreate__assign">
            <div className="d-flex align-items-baseline flex-wrap gap-2">
                <span className="variantGroupCreate__label">{t("variantGroup:whereGoes", "Which version goes where")}</span>
                <span className="text-muted small flex-grow-1">{t("variantGroup:assignHint", "Pick now, or leave “Later” and choose on the calendar card.")}</span>
                <span className="text-muted small">{t("variantGroup:sameEverywhere", "Same version everywhere")}:</span>
                {versions.map((_, i) => (
                    <button key={i} type="button" className="btn btn-sm btn-soft-primary"
                            onClick={() => setPicks(Object.fromEntries(chosen.map((c) => [c.uuid, VERSION_LABELS[i]])))}>
                        {VERSION_LABELS[i]}
                    </button>
                ))}
            </div>
            <table className="table align-middle variantGroupCreate__table">
                <thead>
                <tr>
                    <th>{t("variantGroup:channel", "Channel")}</th>
                    <th>{t("variantGroup:version", "Version")}</th>
                    <th>{t("variantGroup:ownTime", "Own time")}</th>
                </tr>
                </thead>
                <tbody>
                {chosen.map((channel) => {
                    const pick = picks[channel.uuid] ?? LATER;
                    return (
                        <tr key={channel.uuid}>
                            <td>
                                <span className="d-flex align-items-center gap-2">
                                    <ChannelAvatar channel={channel}/>
                                    <span><span className="d-block fw-semibold">{channel.name}</span><span className="d-block text-muted small">{channel.network}</span></span>
                                </span>
                            </td>
                            <td>
                                <span className="d-flex gap-1" role="group" aria-label={t("variantGroup:version", "Version") + " · " + channel.name}>
                                    {versions.map((v, i) => {
                                        const f = fit(channel, v);
                                        const l = VERSION_LABELS[i];
                                        return (
                                            <button key={l} type="button" aria-pressed={pick === l} data-testid={`pick-${channel.uuid}-${l}`}
                                                    title={f.over ? t("variantGroup:overLimit", "over the limit") + ` (${f.length}/${f.max})` : undefined}
                                                    className={"btn btn-sm " + (pick === l ? "btn-primary" : "btn-outline-secondary")}
                                                    onClick={() => setPicks((prev) => ({...prev, [channel.uuid]: l}))}>
                                                {l}{f.over && <span className="text-warning ms-1">!</span>}
                                            </button>
                                        );
                                    })}
                                    <button type="button" aria-pressed={pick === LATER}
                                            className={"btn btn-sm " + (pick === LATER ? "btn-primary" : "btn-outline-secondary")}
                                            onClick={() => setPicks((prev) => ({...prev, [channel.uuid]: LATER}))}>
                                        {t("variantGroup:later", "Later")}
                                    </button>
                                </span>
                            </td>
                            <td>
                                <input type="datetime-local" className="form-control form-control-sm" value={ownTimes[channel.uuid] || ''}
                                       aria-label={t("variantGroup:ownTime", "Own time") + " · " + channel.name}
                                       disabled={pick === LATER}
                                       onChange={(e) => setOwnTimes((prev) => ({...prev, [channel.uuid]: e.target.value}))}/>
                            </td>
                        </tr>
                    );
                })}
                </tbody>
            </table>
        </div>
    );

    const picked = assignments().length;

    return (
        <Modal show onHide={onClose} size="xl" centered className="variantGroupModal variantGroupCreate">
            <Modal.Header closeButton>
                <Modal.Title className="flex-shrink-0 me-3">{t("variantGroup:newGroup", "New post with versions")}</Modal.Title>
                <input className="form-control variantGroupCreate__title" value={title} placeholder={t("variantGroup:title", "Title")}
                       aria-label={t("variantGroup:title", "Title")} onChange={(e) => setTitle(e.target.value)}/>
            </Modal.Header>
            {stepper}
            <Modal.Body>
                {error && <div className="alert alert-danger" role="alert">{error}</div>}
                {step === 'write' ? writeStep : assignStep}
            </Modal.Body>
            <Modal.Footer>
                {step === 'write'
                    ? <>
                        <button type="button" className="btn btn-link" onClick={onClose}>{t("common:cancel", "Cancel")}</button>
                        <button type="button" className="btn btn-primary" disabled={!writeValid} onClick={() => setStep('assign')}>
                            {t("variantGroup:next", "Next: channels & time")}
                        </button>
                    </>
                    : <>
                        <button type="button" className="btn btn-link" disabled={busy || created !== null} onClick={() => setStep('write')}>{t("variantGroup:back", "Back")}</button>
                        <button type="button" className="btn btn-primary" disabled={busy} onClick={submit}>
                            {picked > 0 ? t("variantGroup:createAndReview", "Create & review") : t("variantGroup:create", "Create")}
                        </button>
                    </>}
            </Modal.Footer>
        </Modal>
    );
};

export default VariantGroupCreate;
